import {
  useApiUserApplicationsAnswersList,
  useApiUserApplicationsAnswersDestroy,
  useApiUploadUploadQuestionFileCreate,
  useApiUploadDestroy,
} from "@/api/lctApIComponents";
import { UploadedFile, UploadedFileRequest } from "@/api/lctApISchemas";
import { useState, useEffect } from "react";
import { FileUploadProgress } from "../pvp/components/PVPApplicationFormQuestion/components/UploadQuestion/types";
import { useAppSelector } from "@/hooks/reduxHooks";
import { useToast } from "./use-toast";
import { useUserAppInfo } from "./useUserAppInfo";

const isValidUploadFormData = (formData: FormData): formData is FormData & UploadedFileRequest => {
  return formData.has("uploaded_file") && formData.get("uploaded_file") instanceof Blob;
};

const useUpload = (userAppId: string, questionId?: string) => {
  const token = useAppSelector((state) => state.user.authKey);
  const { toast } = useToast();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [fileUploadProgress, setFileUploadProgress] = useState<FileUploadProgress | undefined>(undefined);
  const [uploading, setUploading] = useState(false);
  const { applicationEditable } = useUserAppInfo(userAppId);

  const {
    data: answersList,
    isLoading: uploadedFilesLoading,
    refetch: refetchFiles,
  } = useApiUserApplicationsAnswersList(
    {
      pathParams: { id: userAppId },
      headers: { Authorization: `Token ${token}` },
    },
    { enabled: !!userAppId, refetchOnMount: false },
  );

  useEffect(() => {
    if (answersList) {
      // Find the answer for the current question and get its uploaded_file_list
      const answer = answersList.find((answer) => answer.application_question === questionId);
      const files = answer?.uploaded_file_list || [];

      setUploadedFiles(files);
    }
  }, [answersList, questionId]);

  const { mutateAsync: uploadFileApi } = useApiUploadUploadQuestionFileCreate();
  const { mutateAsync: deleteAnswerApi } = useApiUserApplicationsAnswersDestroy();
  const { mutateAsync: deleteFileApi } = useApiUploadDestroy();

  const uploadFile = async (file: File, onProgress?: (progress: number) => void) => {
    if (!questionId) {
      throw new Error("Question ID is required");
    }

    // Check if we already have 5 files
    if (uploadedFiles.length >= 5) {
      toast({
        title: "Maximum files reached",
        description: "You can only upload up to 5 files",
        variant: "destructive",
        duration: 2000,
      });
      throw new Error("Maximum of 5 files allowed");
    }

    try {
      setUploading(true);
      setFileUploadProgress({
        progress: 0,
        File: file,
        source: null,
      });
      onProgress?.(0);

      const formData = new FormData();
      formData.append("uploaded_file", file);
      formData.append("user_application", userAppId);
      formData.append("question_id", questionId);

      if (!isValidUploadFormData(formData)) {
        throw new Error("Invalid form data structure");
      }

      const data = await uploadFileApi({
        body: formData,
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      onProgress?.(100);
      await refetchFiles();
      setFileUploadProgress(undefined);
      toast({
        title: "File uploaded",
        variant: "default",
        description: `${file.name} has been uploaded successfully`,
        duration: 2000,
      });
      return data;
    } catch (error) {
      setFileUploadProgress(undefined);
      toast({
        title: "Error uploading file",
        description: "Something went wrong while uploading the file",
        variant: "destructive",
        duration: 2000,
      });
      console.error("Upload failed:", error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const uploadFiles = async (files: FileUploadProgress[], onProgress?: (fileIndex: number, progress: number) => void) => {
    try {
      // Upload files sequentially to avoid overwhelming the server
      const responses = [];
      for (let i = 0; i < files.length; i++) {
        const fileProgress = files[i];
        const response = await uploadFile(fileProgress.File, (progress) => onProgress?.(i, progress));
        responses.push(response);
      }
      return responses;
    } catch (error) {
      console.error("Failed to upload files:", error);
      throw error;
    }
  };

  const deleteFile = async (file: UploadedFile) => {
    try {
      if (!applicationEditable) {
        toast({
          title: "Cannot delete file",
          description: "Files cannot be deleted after the application has been submitted",
          variant: "destructive",
          duration: 3000,
        });
        return;
      }

      await refetchFiles();
      // Find the answer that contains this file in its uploaded_file_list
      const answer = answersList?.find((a) => a.application_question === questionId && a.uploaded_file_list?.some((f) => f.id === file.id));

      setFileUploadProgress(undefined);
      if (!answer) {
        console.error("No answer found for file:", file);
        throw new Error("Answer not found for file");
      }

      // Check if this is the last file in the answer
      const remainingFiles = answer.uploaded_file_list?.filter((f) => f.id !== file.id) || [];

      // Delete the uploaded file first
      await deleteFileApi({
        pathParams: { id: file.id },
        headers: { Authorization: `Token ${token}` },
      });

      // Only delete the answer if there are no files left
      if (remainingFiles.length === 0) {
        await deleteAnswerApi({
          pathParams: {
            id: answer.id,
            userapp: userAppId,
          },
          headers: { Authorization: `Token ${token}` },
        });
      }

      toast({
        title: "File deleted successfully",
        variant: "default",
        duration: 2000,
      });
      await refetchFiles();
    } catch (error) {
      toast({
        title: "Error deleting file",
        description: "Something went wrong while deleting the file",
        variant: "destructive",
        duration: 2000,
      });
      console.error("Error deleting file:", error);
      throw error;
    }
  };

  return {
    uploadedFiles,
    uploadedFilesLoading,
    fileUploadProgress,
    setFileUploadProgress,
    uploadFile,
    uploadFiles,
    uploading,
    deleteFile,
  };
};

export default useUpload;
